import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ActivationEnd, NavigationEnd, Router } from '@angular/router';
import { fromJS } from 'immutable';
import { isEqual } from 'lodash';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { LayoutProviderService, TimePickerService, ValuesExchangeProvider } from '@nrwl/common-lib';
import { NavigationProvider } from '@nrwl/navigation-abstract';

import { IOpenOutletsInfo, LocationParser } from '../../navigation/location.parser';

export interface IEntityConfig {
  [id: string]: {
    method: 'get';
    url: string;
  };
}

@Component({
  selector: 'fusion-ui-perspective',
  templateUrl: './perspective.layout.template.html',
  styleUrls: ['./perspective.layout.style.scss']
})
export class PerspectiveLayoutComponent implements OnInit, OnDestroy {
  routeSubscription: Subscription;
  fragmentSubscription: Subscription;
  queryParamsSubscription: Subscription;
  perspectivesList = [];
  navbarDataSource = []; // nothing at the moment
  openOutlets: IOpenOutletsInfo; // a replacement for toolbars
  dashboardArea = [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  activeLayout: any; // full workspace
  entitiesConfig: IEntityConfig;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  wizards: any;
  wizardOpened = false;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  pageOutletWidth = undefined;
  dashboardOutletWidth = 'none';
  hideOtherLayout = false;
  embedLayout = false;
  constructor(
    private readonly layoutService: LayoutProviderService,
    private readonly router: Router,
    private readonly activeRoute: ActivatedRoute,
    readonly timePickerService: TimePickerService,
    private readonly exchange: ValuesExchangeProvider,
    private readonly navigator: NavigationProvider
  ) {}

  ngOnInit() {
    //TODO this.router.config.push({path: 'perspective/:id', component: PerspectiveLayoutComponent});
    this._getLayout$();
    this.exchange.registerInterest('on_close', this.onCloseWizardListener.bind(this));
  }

  recalculateOpenOutlets(): void {
    const visitor = new LocationParser();
    const newOpenOutlets = visitor.start(this.activeRoute.snapshot);
    if (!isEqual(this.openOutlets, newOpenOutlets)) {
      this.openOutlets = newOpenOutlets;
    }
  }

  //TODO - rewrite this (see up)
  parseInstructions(): void {
    const perspectiveId = this.layoutService.getCurrentPerspective();
    this.perspectivesList = [];
    //eslint-disable-next-line
    this.activeLayout.get('perspectives').forEach((perspectiveRef: any) => {
      const id = perspectiveRef.get('ref').get('id');
      const fullInfo = this.activeLayout.get('registry').get('perspective').get(id).toJS();
      const result = {
        id,
        small_icon_id: fullInfo.small_icon_id,
        large_icon_id: fullInfo.large_icon_id,
        title: fullInfo.description,
        icon: fullInfo.icon,
        category: fullInfo.category,
        active: perspectiveId === id
      };
      this.perspectivesList.push(result);
    });

    const subMenu = [];
    //To Add Wizard Menu on the side Nav Bar
    //eslint-disable-next-line
    this.activeLayout.get('wizards').forEach((wizardRef: any) => {
      const id = wizardRef.get('wizard').get('ref').get('id');

      if (wizardRef.get('add_to_menu')) {
        const wizardInfo = this.activeLayout.get('registry').get('wizard').get(id).toJS();
        const result = {
          icon: wizardInfo.icon,
          id: wizardInfo.id,
          title: wizardInfo.wizardConfig.valuesFixed.menu_name,
          action: undefined
        };
        result.action = () => this.navigateToWizard(result.id);
        subMenu.push(result);
      }
    });
    if (subMenu.length > 0) {
      this.wizards = {
        icon: 'add_new',
        title: 'Add New',
        submenu: subMenu
      };
    }

    this.dashboardArea = [];
    this.activeLayout
      .get('registry')
      .get('perspective')
      .get(perspectiveId)
      .get('areas')
      .forEach(area => {
        if (area.get('category') === 'dashboard') {
          const asJs = area.toJS();
          const result = {
            areaId: asJs.id,
            description: asJs.description,
            views: asJs.views,
            routed_view: asJs.routed_view
          };
          this.dashboardArea.push(result);
        }
      });
  }

  ngOnDestroy() {
    this.routeSubscription.unsubscribe();
    if (this.fragmentSubscription) {
      this.fragmentSubscription.unsubscribe();
    }
    if (this.queryParamsSubscription) {
      this.queryParamsSubscription.unsubscribe();
    }
  }

  navigateToWizard(wizardId: string): void {
    const perspective = this.layoutService.getCurrentPerspective();
    if (this.wizardOpened) {
      this.exchange.notifyListeners('route_guard', {
        wizardId,
        perspective
      });
    } else {
      this.navigator.navigateToWizard([wizardId]);
    }
    this.wizardOpened = true;
  }

  onCloseWizardListener(): void {
    this.wizardOpened = false;
  }

  private _getLayout$() {
    // why it is not enough to be subscribed to ActivatedRoute params? This will only notify you
    // about changes to route you have matched from app.routing.ts. You won't be notified about
    // changes in child outlets (eg, perspective stays the same, but page is different).
    // As each page may require slightly different toolbars, we have to listen on any URL changes
    // and react to this.
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd || event instanceof ActivationEnd))
      //eslint-disable-next-line
      .subscribe(event => {
        this.recalculateOpenOutlets();
      });

    this.routeSubscription = this.activeRoute.params.subscribe(values => {
      this.layoutService.getLayout().then(layout => {
        this.navbarDataSource = layout.navbarData;
        this.navbarDataSource['title'] = 'Fusion';
        this.activeLayout = fromJS(layout);
        this.entitiesConfig = layout.entities;
        // make sure we update which perspective is active now
        this.layoutService.setCurrentPerspective(values.id);
        this.recalculateOpenOutlets();
        const outletToActivate = this.activeRoute.snapshot.fragment;
        this.parseInstructions();
      });
    });
  }
}
